import clsx from 'clsx'
import type React from 'react'
import { Link } from './link'

export type AreaTreemapItem = {
  id: string
  label: string
  /** Secondary line, e.g. "12 fields · 34.5 ac". */
  detail?: string
  /** Relative size of the tile (e.g. acres). */
  value: number
  href?: string
  /** Optional controls shown on hover, e.g. an archive button. */
  actions?: React.ReactNode
}

// Every tile has the same aspect ratio, so tile area is proportional to `value` and a smaller item
// looks like a smaller copy of a bigger one rather than a thinner strip.
const LARGEST_TILE_REM = 15
const ASPECT_RATIO = '4 / 3'
// Tiny (or empty) items would vanish; never render a tile narrower than this share of the largest one.
// Real values stay in the labels.
const MIN_WIDTH_SHARE = 0.35

const PALETTE = [
  'bg-green-700 hover:bg-green-600',
  'bg-emerald-700 hover:bg-emerald-600',
  'bg-teal-700 hover:bg-teal-600',
  'bg-lime-700 hover:bg-lime-600',
  'bg-green-800 hover:bg-green-700',
  'bg-emerald-800 hover:bg-emerald-700',
]

export function AreaTreemap({ items, className }: { items: AreaTreemapItem[]; className?: string }) {
  const sorted = [...items].sort((a, b) => b.value - a.value)
  const largest = sorted[0]?.value ?? 0

  return (
    <div className={clsx('flex flex-wrap items-end gap-3', className)}>
      {sorted.map((item, index) => {
        // Width scales with sqrt(value) because area = width × height and the aspect ratio is fixed.
        const share = largest > 0 ? Math.max(Math.sqrt(item.value / largest), MIN_WIDTH_SHARE) : 1
        // The name always shows (truncated on a small tile): a new farm with no fields has no area and would otherwise be nameless.
        const showLabel = true
        const showDetail = share > 0.65
        const title = [item.label, item.detail].filter(Boolean).join(' — ')
        const tileClass = clsx(
          'relative flex size-full flex-col justify-end overflow-hidden rounded-md p-2 text-white transition-colors',
          PALETTE[index % PALETTE.length]
        )
        const content = (
          <>
            {showLabel && <span className="truncate text-sm/5 font-semibold">{item.label}</span>}
            {showDetail && item.detail && <span className="truncate text-xs/5 text-white/75">{item.detail}</span>}
          </>
        )
        return (
          <div
            key={item.id}
            className="group relative max-w-full"
            style={{ width: `${share * LARGEST_TILE_REM}rem`, aspectRatio: ASPECT_RATIO }}
          >
            {item.href ? (
              <Link href={item.href} title={title} className={tileClass}>
                {content}
              </Link>
            ) : (
              <div title={title} className={tileClass}>
                {content}
              </div>
            )}
            {item.actions && (
              <div className="absolute top-2 right-2 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                {item.actions}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
